import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import { submitPasscode } from 'api/client';
import { updateShowPasswordQualifyStatus } from 'client/redux/modules/promoState';
import { setNeedEnterPasscode } from 'client/redux/modules/userEntryState';
import { showCustomErrDialog, handlers } from 'client/redux/modules/systemErrorDialog';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import { hasShouldNotEnterPasscodeEachTimeGames } from 'config/promoGameTypesClientConfig';
import * as clientErrTypes from 'config/clientErrorTypes';
import { getErrorTypeDialogDesc, getPasscodeErrorType, errorTypes } from 'config/passcode';

export default function useSubmitPasscode() {
    const dispatch = useDispatch();
    const errHandler = useClientErrorHandler();
    const { uuid: promoUUID, type: gameType } = useSelector(s => s.promo);
    const { currentEntryUUID, isNeedEnterPasscode } = useSelector(s => s.userEntryState);
    const { passcode } = useSelector(s => s.promoState);

    const func = useCallback(async () => {
        if (Boolean(isNeedEnterPasscode)) {
            try {
                const resp = await submitPasscode({
                    promoUUID,
                    entryUUID: currentEntryUUID,
                    passcode
                });
                const { payload, httpStatus } = resp;
                const remainingCounts = payload?.remainingCounts;
                if (httpStatus === 200) {
                    dispatch(
                        updateShowPasswordQualifyStatus({
                            hasUsablePasscode: Boolean(remainingCounts),
                            passcode: remainingCounts >= 1 ? passcode : '',
                            passcodeRemainingCount: remainingCounts
                        })
                    );
                    if (hasShouldNotEnterPasscodeEachTimeGames(gameType)) {
                        dispatch(setNeedEnterPasscode(false));
                    }
                    return { hasUsablePasscode: Boolean(remainingCounts), passcode };
                } else if (
                    [
                        errorTypes.DUPLICATE,
                        errorTypes.USAGE_LIMIT_EXCEEDED,
                        errorTypes.ERROR
                    ].includes(getPasscodeErrorType(resp))
                ) {
                    // [#86ervq65t] passcode flow UX 改動
                    // 如果無法辨識登入者（user 沒登入或沒有cookie）使用過這個 pcode，才顯示說明訊息。
                    // User B 嘗試使用 User A 的 pcode 參加活動，會顯示說明訊息。
                    dispatch(
                        showCustomErrDialog({
                            title: i18n.t('label.system.unavailable'),
                            desc: getErrorTypeDialogDesc(getPasscodeErrorType(resp)),
                            handler: handlers.PASSCODE_DENIED
                        })
                    );
                    return {
                        hasUsablePasscode: false,
                        passcode: '',
                        error: getPasscodeErrorType(resp)
                    };
                } else {
                    throw generateApiErrObj(resp, clientErrTypes.SUBMIT_PASSCODE);
                }
            } catch (error) {
                errHandler({ err: error });
            }
        }
        return { hasUsablePasscode: false, passcode: '' };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoUUID, currentEntryUUID, isNeedEnterPasscode, passcode]);

    return func;
}
